import * as React from "react";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import PartnerBrand from "../../../model/partnerBrand/PartnerBrand";
import {RootState} from "../../../store/reducer/root/RootState";
import {fetchPartnerBrand} from "../../../store/action/partnerBrand/partnerBrand";
import {api} from "../../../api/api";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import Input from "../../../hiberUiKit/src/atoms/Input/Input";
import Row from "../../layout/Row";
import Col from "../../layout/Col";
import TextUtils from "../../../utils/TextUtils";
import Toast from "../../helper/Toast";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {PartnerBrandSubsectionTitle} from "./sections/PartnerBrandSectionTitle";

interface OwnProps {
	partnerBrand: PartnerBrand;
	/**
	 * Whether the form around this section holds edits not saved yet. The checklist reads the saved
	 * partner, so it says so instead of contradicting what the admin has just typed.
	 */
	hasUnsavedChanges?: boolean;
}

interface Props extends OwnProps {
	partnersPortalUrl: string;
	onGenerated: () => void;
}

const warningRowStyle: React.CSSProperties = {display: "flex", alignItems: "center", gap: "10px", background: "#fff7e6", borderRadius: "6px", padding: "8px 10px", marginBottom: "6px"};

function WarningIcon() {
	return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a35f00" strokeWidth="2.5"
				strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/>
	</svg>;
}

// Readiness always reflects the saved partner, independently of the editable form around it.
export class FleetMileageCaptureSection extends React.PureComponent<Props, {generating: boolean}> {
	state = {generating: false};
	private generating = false;
	private mounted = false;

	componentDidMount() { this.mounted = true; }
	componentWillUnmount() { this.mounted = false; }

	private generate = () => {
		if (this.generating) return;
		this.generating = true;
		this.setState({generating: true});
		api.instance.partnerBrandApi.generateFleetForm(this.props.partnerBrand.id)
			.then(() => { if (this.mounted) this.props.onGenerated(); })
			.catch(e => notifyBugsnagAndShowToast(e, "Generate fleet link"))
			.then(() => {
				this.generating = false;
				if (this.mounted) this.setState({generating: false});
			});
	};

	private copy = (link: string) => {
		TextUtils.copyToClipboard(link);
		Toast.success("Link copied");
	};

	render() {
		const formId = this.props.partnerBrand.fleetFormId.orNull;
		return <div style={{marginBottom: "30px"}}>
			<PartnerBrandSubsectionTitle title={"Fleet mileage capture"} first={true}/>
			{!formId && <ButtonPrimary label="Generate fleet link" onClick={this.generate}
									   disabled={this.state.generating} isLoading={this.state.generating}/>}
			{/* Until there is a link there is nothing to send, so most partners, who never need one, see no checklist. */}
			{formId && this.renderLink()}
		</div>;
	}

	private renderLink() {
		const partner = this.props.partnerBrand;
		const link = this.link();
		// Only what is missing: a list of what is fine buries the one line that needs doing.
		const missing = [
			{ready: !!partner.logoId.orNull, warning: "No logo: the form will show no partner branding."},
			{ready: partner.hasSignature, warning: "No signature: waiver PDFs for fleet submissions will be unsigned."},
			{ready: !!partner.leadUserId.orNull, warning: "No Lead User: waiver PDFs for fleet submissions will have no submitter name."}
		].filter(check => !check.ready);
		return <div>
			{!link && <div style={warningRowStyle} role="status">
				<WarningIcon/><span>Partner portal URL is not configured</span>
			</div>}
			{link && <Row>
				<Col lg={8} xl={8}>
					{/* Not a FormGroup: its bottom margin holds room for a validation error a read-only link never has. */}
					<label className="m-form-group_label" htmlFor="fleet-form-link">Fleet link</label>
					<Input id="fleet-form-link" type="text" readonly value={link} onFocus={e => e.currentTarget.select()}/>
				</Col>
				<Col lg={4} xl={4}>
					<div className={"c-pb-fleet-copy"}>
						<ButtonPrimary label="Copy link" onClick={() => this.copy(link)}/>
					</div>
				</Col>
			</Row>}
			{missing.length > 0 &&
			<ul style={{listStyle: "none", margin: "16px 0 0", padding: 0}} aria-label="Missing for the fleet form">
				{missing.map(check => <li key={check.warning} style={warningRowStyle}>
					<WarningIcon/><span>{check.warning}</span>
				</li>)}
			</ul>}
			{/* The checklist reads the saved partner, so it says so rather than contradict what was just typed. */}
			{missing.length > 0 && this.props.hasUnsavedChanges &&
			<p style={{margin: "4px 0 0", fontSize: "12px", color: "#8a5300"}}>As last saved. You have unsaved changes.</p>}
		</div>;
	}

	/**
	 * The full link, or empty when the partner has none yet or the portal URL is not configured.
	 */
	private link(): string {
		const portalUrl = this.props.partnersPortalUrl.trim().replace(/\/+$/, "");
		const formId = this.props.partnerBrand.fleetFormId.orNull;
		return portalUrl && formId ? `${portalUrl}/fleet?formId=${encodeURIComponent(formId)}` : "";
	}
}

function mapStateToProps(state: RootState) {
	return {partnersPortalUrl: state.settings.settings.map(settings => settings.partnersPortalUrl).getOrElse("")};
}

function mapDispatchToProps(dispatch: Dispatch<any>, ownProps: OwnProps) {
	return {
		onGenerated: () => dispatch(fetchPartnerBrand(ownProps.partnerBrand.id,
			e => notifyBugsnagAndShowToast(e, "Fetch partner brand")))
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(FleetMileageCaptureSection);
